import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Check, AlertCircle, Loader2 } from 'lucide-react';
import { api } from '@/services/api';
import { CompactInput } from '@/components/CompactInput';

/** Channel info from Podbean */
interface PodbeanChannel {
  id: string;
  title: string;
  logo?: string;
  website?: string;
}

interface PodbeanCredentialsFormProps {
  /** Current client ID value */
  clientId: string;
  /** Whether a secret is already set (shown as [ENCRYPTED] from API) */
  secretIsSet: boolean;
  /** Currently selected channel */
  channel?: PodbeanChannel | null;
  /** Callback when credentials change (for parent component change detection) */
  onChange: (clientId: string, clientSecret: string) => void;
  /** Callback when channel selection changes */
  onChannelChange?: (channel: PodbeanChannel | null) => void;
  /** Whether the form is disabled */
  disabled?: boolean;
  /** Company ID for testing stored credentials */
  companyId?: string;
}

/**
 * Form component for managing PodBean OAuth credentials.
 * Used in Podcast Manager settings.
 *
 * Features:
 * - Client ID is always visible and editable
 * - Client Secret shows placeholder if set, only sends new value if changed
 * - Test Connection button validates credentials and fetches available channels
 * - Channel selector shown when multiple channels available
 */
export const PodbeanCredentialsForm: React.FC<PodbeanCredentialsFormProps> = ({
  clientId: initialClientId,
  secretIsSet,
  channel,
  onChange,
  onChannelChange,
  disabled = false,
  companyId,
}) => {
  // Form state
  const [clientId, setClientId] = useState(initialClientId);
  const [clientSecret, setClientSecret] = useState('');

  // Channel state
  const [channels, setChannels] = useState<PodbeanChannel[]>([]);
  const [showChannelPicker, setShowChannelPicker] = useState(false);

  // UI state
  const [testing, setTesting] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Sync initial values when props change
  useEffect(() => {
    setClientId(initialClientId);
    setClientSecret('');
  }, [initialClientId, secretIsSet]);

  // Notify parent of changes
  const handleClientIdChange = (value: string) => {
    setClientId(value);
    onChange(value, clientSecret);
  };

  const handleSecretChange = (value: string) => {
    setClientSecret(value);
    onChange(clientId, value);
  };

  // Handle channel selection
  const handleChannelSelect = (channelId: string) => {
    const selected = channels.find((ch) => ch.id === channelId);
    if (selected && onChannelChange) {
      onChannelChange(selected);
    }
  };

  // Test connection with PodBean
  const handleTestConnection = async () => {
    setError(null);
    setTestSuccess(false);
    setSuccessMessage(null);
    setShowChannelPicker(false);

    if (!clientId) {
      setError('Client ID is required to test connection');
      return;
    }

    // If secret is already set and user hasn't entered a new one, we need companyId to test
    const useStoredCredentials = secretIsSet && !clientSecret;
    if (useStoredCredentials && !companyId) {
      setError('Please enter the Client Secret to test the connection');
      return;
    }

    // If no secret and not using stored credentials, require secret
    if (!clientSecret && !useStoredCredentials) {
      setError('Client Secret is required to test connection');
      return;
    }

    try {
      setTesting(true);

      const response = await api.post<{
        message: string;
        channels: PodbeanChannel[];
      }>('/episodes/podbean/test', {
        clientId,
        clientSecret: clientSecret || undefined,
        companyId: useStoredCredentials ? companyId : undefined,
      });

      if (response.success && response.data) {
        const { channels: fetchedChannels } = response.data;
        setChannels(fetchedChannels);
        setTestSuccess(true);

        if (fetchedChannels.length === 1) {
          // Auto-select single channel
          if (onChannelChange) {
            onChannelChange(fetchedChannels[0]);
          }
          setSuccessMessage(`Connected! Channel: ${fetchedChannels[0].title}`);
        } else if (fetchedChannels.length > 1) {
          // Show picker for multiple channels
          setShowChannelPicker(true);
          setSuccessMessage(
            `Connected! ${fetchedChannels.length} channels available - please select one.`
          );
        } else {
          setSuccessMessage('Connected! No channels found.');
        }

        setTimeout(() => {
          setTestSuccess(false);
          setSuccessMessage(null);
        }, 10000);
      } else {
        setError(response.error?.message || 'Connection test failed');
      }
    } catch (err: any) {
      const message =
        err.response?.data?.error?.message ||
        err.message ||
        'Failed to test connection';
      setError(message);
    } finally {
      setTesting(false);
    }
  };

  const isDisabled = disabled || testing;

  return (
    <div className="space-y-3">
      {/* Compact credential fields */}
      <div className="space-y-0.5">
        <CompactInput
          label="Client ID"
          value={clientId}
          onChange={handleClientIdChange}
          placeholder="Enter your PodBean Client ID"
          disabled={isDisabled}
        />
        <CompactInput
          label={secretIsSet ? 'Secret (set)' : 'Client Secret'}
          value={clientSecret}
          onChange={handleSecretChange}
          type="password"
          placeholder={secretIsSet ? 'Enter new to change' : 'Enter Client Secret'}
          disabled={isDisabled}
        />
      </div>

      {/* Selected Channel Badge */}
      {channel && (
        <div className="flex items-center gap-2 ml-5">
          <span className="text-sm text-muted-foreground">Channel:</span>
          <Badge variant="outline" className="text-green-600 border-green-200">
            <Check className="h-3 w-3 mr-1" />
            {channel.title}
          </Badge>
        </div>
      )}

      {/* Channel Picker (shown after successful test with multiple channels) */}
      {showChannelPicker && channels.length > 1 && (
        <div className="ml-5 space-y-2">
          <Label htmlFor="podbean-channel">Select Podcast Channel</Label>
          <Select
            value={channel?.id || ''}
            onValueChange={handleChannelSelect}
            disabled={isDisabled}
          >
            <SelectTrigger id="podbean-channel" className="w-full max-w-xs">
              <SelectValue placeholder="Select a channel..." />
            </SelectTrigger>
            <SelectContent>
              {channels.map((ch) => (
                <SelectItem key={ch.id} value={ch.id}>
                  {ch.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Test Connection */}
      <div className="flex items-center gap-3 ml-5">
        <Button
          variant="outline"
          size="sm"
          onClick={handleTestConnection}
          disabled={isDisabled || !clientId}
        >
          {testing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Testing...
            </>
          ) : (
            'Test Connection'
          )}
        </Button>
      </div>

      {/* Test Success Message */}
      {testSuccess && successMessage && (
        <Alert className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 ml-5">
          <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">
            {successMessage}
          </AlertDescription>
        </Alert>
      )}

      {/* Error Message */}
      {error && (
        <Alert variant="destructive" className="ml-5">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default PodbeanCredentialsForm;
